import { computed, inject, Injectable } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { VirtualFenceService } from '@vectronic/base-map';
import { dsDtos, dsEnums } from '@vectronic/openApi-customer';
import {
  DeviceTypeParameterConfigWithAvailableListAndParameter,
  ParameterDataService,
  ScheduleHelp,
} from '@vectronic/util';
import { cloneDeep } from 'es-toolkit';
import { BehaviorSubject, combineLatest, debounceTime, filter, map, of, shareReplay } from 'rxjs';
import { CameraScheduleService } from '../camera-schedule.service';
import { RemoteCommandProviderService } from '../remote-command-provider.service';
import { ScheduleServices } from '../schedule.service';
import { DropOffCommandService } from './drop-off-command.service';

@Injectable({ providedIn: 'root' })
export class RemoteCommandsService {
  private remoteCommandProviderService = inject(RemoteCommandProviderService, { optional: true });
  private scheduleServices = inject(ScheduleServices);
  private cameraScheduleService = inject(CameraScheduleService);
  private virtualFenceService = inject(VirtualFenceService);
  private parameterDataService = inject(ParameterDataService);
  private dropOffCommandService = inject(DropOffCommandService);

  availableDeviceFeatures$ = this.remoteCommandProviderService?.availableDeviceFeatures$ ?? of();
  ignoreShortHeaderMessage$ = new BehaviorSubject(false);

  availableDeviceFeatures = toSignal(this.availableDeviceFeatures$);
  selectedDropOffData = toSignal(this.dropOffCommandService.selectedDropOffData$);
  selectedCollars = toSignal(this.remoteCommandProviderService?.selectedCollars$ ?? of());
  parameters = toSignal(this.remoteCommandProviderService?.parameters$ ?? of());
  deviceTypes = toSignal(this.remoteCommandProviderService?.deviceTypes$ ?? of());
  cameraResponse = toSignal(this.remoteCommandProviderService?.cameraResponse$ ?? of());
  ignoreShortHeaderMessage = toSignal(this.ignoreShortHeaderMessage$);

  selectedCollarIds$ = toObservable(this.remoteCommandProviderService?.selectedCollarIds);
  isChanged = computed(() => {
    const isChangedExceptCamera = ScheduleHelp.services()?.some((type) =>
      this.scheduleServices[type]?.isChanged(),
    );
    const isChangedCamera = this.scheduleServices.CAMERA.isChanged();
    return (
      isChangedExceptCamera ||
      isChangedCamera ||
      this.virtualFenceService.isVirtualFenceChanged() ||
      this.parameterDataService.isChanged()
    );
  });

  isFilled = computed(() => {
    const isFilledExceptCamera = ScheduleHelp.services()?.some((type) =>
      this.scheduleServices[type]?.isFilledAndChanged(),
    );
    const isFilledCamera = this.scheduleServices.CAMERA.isFilledAndChanged();
    return (
      isFilledExceptCamera ||
      isFilledCamera ||
      this.virtualFenceService.isVirtualFenceChanged() ||
      this.parameterDataService.isChanged()
    );
  });

  hasMultipleCameraTypes = computed(() => {
    const selectedIds = this.remoteCommandProviderService?.selectedCollarIds();
    const cameraResponse = this.cameraResponse();
    const deviceTypes = this.deviceTypes();

    if (!selectedIds?.length || !cameraResponse || !deviceTypes?.length) return false;

    const cameraTypes = new Set<string>();
    selectedIds.forEach((id) => {
      const camera = cameraResponse[id];
      if (camera?.deviceTypeId) {
        const type = deviceTypes.find((dt) => dt.id === camera.deviceTypeId);
        if (type) {
          cameraTypes.add(type.id);
        }
      }
    });

    return cameraTypes.size > 1;
  });

  deviceTypeParameterConfig$ = combineLatest([
    this.remoteCommandProviderService?.remoteParameters$ ?? of(),
    this.remoteCommandProviderService?.parameters$ ?? of(),
  ]).pipe(
    filter(([rp, p]) => !!rp && !!p),
    map(([deviceTypeParameterConfig, parameters]) => {
      if (!deviceTypeParameterConfig) return null;
      return deviceTypeParameterConfig.map((parameterResponse) => {
        const ret = parameterResponse as DeviceTypeParameterConfigWithAvailableListAndParameter;
        ret.parameter = parameters.find((p) => p.id === parameterResponse.parameterId);
        return ret;
      });
    }),
    shareReplay(1),
  );

  userOrFactory$ = new BehaviorSubject<'customer' | 'allUser' | 'allFactory'>('customer');
  basicOrAdvanced$ = new BehaviorSubject<'basic' | 'advanced'>('basic');
  readonly$ = new BehaviorSubject(false);
  remoteSchedules = toSignal(this.remoteCommandProviderService?.remoteSchedules$ ?? of());

  remoteSchedule$ = this.remoteCommandProviderService?.remoteSchedules$.pipe(
    map((scheduleResponse) => {
      const selectedCollarIds = this.remoteCommandProviderService?.selectedCollarIds();
      if (!selectedCollarIds?.length || !scheduleResponse) return;
      ScheduleHelp.services().forEach((type) => {
        if (this.scheduleServices[type]?.isFilledAndChanged()) {
          const supportedIds = new Set(scheduleResponse?.[type] || []);
          const isSupportedByAll = selectedCollarIds.every((id) => supportedIds.has(id));
          if (!isSupportedByAll) {
            this.scheduleServices[type].onRemoveSchedule();
          }
        }
      });

      if (this.cameraScheduleService?.isFilledAndChanged()) {
        const supportedIds = new Set(scheduleResponse?.CAMERA || []);
        const isSupportedByAll = selectedCollarIds.every((id) => supportedIds.has(id));
        if (!isSupportedByAll) {
          this.cameraScheduleService.onRemoveSchedule();
        }
      }

      if (this.virtualFenceService.isVirtualFenceFilled()) {
        const supportedIds = new Set(scheduleResponse?.VIRTUAL_FENCE || []);
        const isSupportedByAll = selectedCollarIds.every((id) => supportedIds.has(id));
        if (!isSupportedByAll) {
          this.virtualFenceService.resetVirtualFence();
        }
      }
    }),
  );

  remoteParameter$ = this.deviceTypeParameterConfig$.pipe(
    map((paramConfigs) => {
      const selectedCollarIds = this.remoteCommandProviderService?.selectedCollarIds();
      if (!selectedCollarIds?.length || !paramConfigs) return;
      const changedParameterDataList = this.parameterDataService.changedParameterDataList();
      if (changedParameterDataList.length > 0) {
        const updatedList = changedParameterDataList.filter((pd) => {
          const config = paramConfigs?.find((c) => c.parameterId === pd.parameterId);
          if (!config) return Boolean(this.remoteCommandProviderService?.isRemote());
          const availableSet = new Set(config.availableForDevices || []);
          return selectedCollarIds.every((id) => availableSet.has(id));
        });
        if (updatedList.length !== changedParameterDataList.length) {
          this.parameterDataService.changedParameterDataList.set(updatedList);
        }
      }
    }),
  );

  deviceTypeParameterConfig = toSignal(this.deviceTypeParameterConfig$);
  virtualFenceConstructionRules$ = toObservable(this.virtualFenceService.constructionRules);

  validator$ = combineLatest([
    this.selectedCollarIds$,
    this.userOrFactory$,
    this.remoteSchedule$,
    this.remoteParameter$,
    ...ScheduleHelp.services().map((type) => this.scheduleServices[type].rulesUpdatedAndFilled$),
    ...ScheduleHelp.services().map((type) => this.scheduleServices[type].isRemovedSchedule$),
    this.cameraScheduleService.rulesUpdatedAndFilled$,
    this.parameterDataService.parameterDataList$,
    this.parameterDataService.updatedParameterDataList$,
    this.virtualFenceConstructionRules$,
  ]).pipe(debounceTime(2000));

  updateParameterDataList(parameterResponse: dsDtos.ParameterData[], updateDefault = true) {
    const parameters = this.parameters() || [];
    const parameterResponseWithParameters = parameterResponse.map((parameterResponse) => {
      const ret = parameterResponse as DeviceTypeParameterConfigWithAvailableListAndParameter;
      ret.parameter = parameters.find((p) => p.id === parameterResponse.parameterId);
      return ret;
    });
    const parameterDataList = parameterResponseWithParameters.map(({ parameter }) => {
      const existing = parameterResponse.find((p) => p.parameterId === parameter?.id);
      const preserveUserData =
        !this.remoteCommandProviderService?.isRemote() &&
        this.remoteCommandProviderService?.isAdmin;
      return {
        parameter,
        parameterId: parameter?.id,
        data: existing?.data,
        isUserData: preserveUserData
          ? (existing?.isUserData ?? parameter?.isAvailableAsUserParameter)
          : parameter?.isAvailableAsUserParameter,
      };
    });
    this.parameterDataService.init(parameterDataList, updateDefault);
  }

  hasNotShortHeaderFormat = computed(() => {
    return this.availableDeviceFeatures()?.some(
      (deviceFeature) => !deviceFeature.features?.includes('SHORT_HEADER_MESSAGE_FORMAT'),
    );
  });

  collarsWithNoShortHeaderFormat = computed(() => {
    return this.availableDeviceFeatures()
      ?.filter(({ features }) => !features?.includes('SHORT_HEADER_MESSAGE_FORMAT'))
      .map(({ idDevice }) => idDevice);
  });

  hasHighActivityCameraTrigger = computed(() => {
    return this.availableDeviceFeatures()?.some((deviceFeature) =>
      deviceFeature.features?.includes('HIGH_ACTIVITY_CAMERA_TRIGGER'),
    );
  });

  isGlobalstarSelected = computed(() => {
    const collars = this.selectedCollars();
    const selectedIds = this.remoteCommandProviderService?.selectedCollarIds();
    if (!collars || !selectedIds?.length) return false;
    const selectedSet = new Set(selectedIds);
    return collars.filter((c) => selectedSet.has(c.id)).some((c) => c.comType === 'GLOBALSTAR');
  });

  isVertexMiniGlobalstar = computed(() => {
    const collars = this.selectedCollars();
    const selectedIds = this.remoteCommandProviderService?.selectedCollarIds();
    if (!collars || !selectedIds?.length) return false;
    const selectedSet = new Set(selectedIds);
    return collars
      .filter((c) => selectedSet.has(c.id))
      .some((c) => {
        const name = (c.deviceTypeCustomerName ?? c.name ?? '').toLowerCase();
        return name.includes('vertex mini') && c.comType === 'GLOBALSTAR';
      });
  });

  generateCommand(): dsDtos.Command[] {
    return this.remoteCommandProviderService
      .selectedCollarIds()
      ?.map((collarId) => {
        const command: dsDtos.Command = {
          deviceClassWithOwnIdPool: 'COLLAR',
          idDevice: collarId,
        } as dsDtos.Command;

        const schedules: dsDtos.Schedule[] = ScheduleHelp.services()
          ?.filter((type) => this.scheduleServices[type]?.isFilledAndChanged())
          ?.map((scheduleType) => {
            const originalRules = Object.values(
              this.scheduleServices[scheduleType].rules$.value,
            ).filter(Boolean);
            const rules = originalRules?.map((r) => cloneDeep(r));
            const collars = this.selectedCollars();
            const collar = collars?.find((c) => c.id === collarId);
            const collarPermanentEnd = collar?.comType === 'GSM' ? '2038-01-01' : '2050-01-01';
            rules?.forEach((rule) => {
              if (rule?.endTime === '2050-01-01' || rule?.endTime === '2038-01-01') {
                rule.endTime = collarPermanentEnd;
              }
            });
            return {
              scheduleType,
              userDefined:
                this.userOrFactory$.value === 'customer' || this.userOrFactory$.value === 'allUser',
              rules,
            } as unknown as dsDtos.Schedule;
          });

        if (this.cameraScheduleService.isFilledAndChanged()) {
          schedules.push({
            scheduleType: 'CAMERA',
            userDefined:
              this.userOrFactory$.value === 'customer' || this.userOrFactory$.value === 'allUser',
            cameraScheduleRules: Object.values(this.cameraScheduleService.rules$.value).filter(
              Boolean,
            ),
          } as unknown as dsDtos.Schedule);
        }

        if (schedules?.length) command.schedules = schedules;

        const paramDataList = this.parameterDataService
          .updatedParameterDataList()
          .filter(({ data }) => data);
        paramDataList?.forEach((parameter) => {
          parameter.isUserData =
            this.userOrFactory$.value === 'customer' || this.userOrFactory$.value === 'allUser';
        });
        if (paramDataList?.length) command.parameterDataList = paramDataList as any;

        const allPolygons = this.virtualFenceService.allPolygons();
        if (this.virtualFenceService.isVirtualFenceChanged()) {
          command.virtualFenceConstructionRules =
            this.virtualFenceService.prepareVirtualFenceRules(allPolygons) || [];
        }

        if (
          command.schedules?.length ||
          command.parameterDataList?.length ||
          command.virtualFenceConstructionRules
        ) {
          return command;
        }
        return undefined;
      })
      .filter(Boolean);
  }

  numberOfFilledSchedules = computed(() => {
    const numberOfFilledSchedulesExceptCamera = ScheduleHelp.services().filter((type) =>
      this.scheduleServices[type]?.isFilledAndChanged(),
    )?.length;
    if (this.cameraScheduleService.isFilledAndChanged()) {
      return numberOfFilledSchedulesExceptCamera + 1;
    }
    return numberOfFilledSchedulesExceptCamera;
  });

  isScheduleFilled = computed(() => {
    return this.numberOfFilledSchedules() > 0;
  });

  numberOfFilledParameters = computed(() => {
    return this.parameterDataService.changedParameterDataList().filter(({ data }) => data).length;
  });
  isParameterFilled = computed(() => {
    return this.numberOfFilledParameters() > 0;
  });

  numberOfFilledDropOffs = computed(() => {
    return this.selectedDropOffData()?.length;
  });

  isDropOffFilled = computed(() => {
    return this.numberOfFilledDropOffs() > 0;
  });

  isMaxSchedulesReached = computed(() => {
    if (this.ignoreShortHeaderMessage()) return false;
    const hasShortHeaderFormat = this.hasNotShortHeaderFormat();
    return (
      hasShortHeaderFormat && (this.numberOfFilledSchedules() >= 1 || this.isParameterFilled())
    );
  });

  isMaxDropOffsReached = computed(() => {
    if (this.ignoreShortHeaderMessage()) return false;
    const hasShortHeaderFormat = this.hasNotShortHeaderFormat();
    return hasShortHeaderFormat && this.numberOfFilledDropOffs() > 1;
  });

  isMaxParametersReached = computed(() => {
    if (this.ignoreShortHeaderMessage()) return false;
    const hasShortHeaderFormat = this.hasNotShortHeaderFormat();
    return hasShortHeaderFormat && this.isScheduleFilled();
  });

  generateDropOffCommand() {
    return this.dropOffCommandService.selectedDropOffData$.value
      ?.map(({ dropOffExtended, dropOffId, idDevice }) => {
        const selectedDropOffId = dropOffExtended?.id ?? dropOffId;
        if (!selectedDropOffId) return;
        const command: dsDtos.Command = { deviceClassWithOwnIdPool: 'COLLAR', idDevice };
        const responseValue = this.dropOffCommandService.remoteDeviceCommands();
        let deviceCommandType: dsEnums.DeviceCommandType;
        if (responseValue?.DROP_OFF_RELEASE?.includes(idDevice))
          deviceCommandType = 'DROP_OFF_RELEASE';
        if (responseValue?.DROP_OFF_RELEASE_LONG?.includes(idDevice))
          deviceCommandType = 'DROP_OFF_RELEASE_LONG';
        if (deviceCommandType) {
          command.deviceCommand = { data: selectedDropOffId.toString(), deviceCommandType };
          return command;
        }
        return undefined;
      })
      .filter(Boolean);
  }
}
